import React from 'react';
import { ArrowRight } from 'lucide-react';

/**
 * A porta de entrada do grupo: qual sistema abrir.
 *
 * A versão anterior espremia quatro colunas de texto dentro de um container
 * estreito — as chamadas quebravam no meio ("Acessar Finance / ERP"), cada
 * cartão trazia quatro linhas de recursos e três emblemas competindo entre si.
 * Parecia página de preços, não porta de entrada.
 *
 * Aqui a tela volta a ser o que é: uma escolha entre quatro coisas. Fundo
 * escuro e quieto para as cores das marcas aparecerem, um cartão por produto
 * com nome, uma frase e três capacidades — e uma única ação clara. O que está
 * indisponível fica visivelmente contido, em vez de disputar atenção.
 */

/**
 * O Atlas Sales (CRM) ainda roda só na máquina de quem desenvolve.
 * Quando ganhar endereço próprio, é esta linha que muda.
 *
 * O caminho /entrar-local abre a sessão sem pedir senha de novo. Do outro lado
 * isso só vale se três coisas baterem: o atalho estar ligado no .env, a chamada
 * vir da própria máquina e o e-mail estar na lista de permitidos. Em produção o
 * atalho fica desligado e o endereço passa a ser o da aplicação publicada.
 */
const ENDERECO_ATLAS_SALES = 'http://localhost:5020/entrar-local';

interface ProductSelectorProps {
  userName: string;
  onSelectProduct: (product: 'escalone' | 'finance' | 'atlas') => void;
}

type Produto = {
  id: string;
  marca: string;              // a letra do logo
  nome: string;
  area: string;               // lido junto do nome: "Atlas Sales · CRM"
  resumo: string;
  recursos: string[];
  acao: string;
  cor: string;                // cor da marca, usada no logo e no brilho
  corTexto: string;
  disponivel: boolean;
  abrir: () => void;
};

const saudacao = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Bom dia';
  if (h < 18) return 'Boa tarde';
  return 'Boa noite';
};

const ProductSelector: React.FC<ProductSelectorProps> = ({ userName, onSelectProduct }) => {
  const primeiroNome = userName?.split(' ')[0] || 'Usuário';

  const produtos: Produto[] = [
    {
      id: 'escalone',
      marca: 'E',
      nome: 'Escalone',
      area: 'Gestão',
      resumo: 'Fluxos de trabalho, aprovações e o time inteiro num lugar só.',
      recursos: ['Fluxos & Kanban', 'Automações & integrações', 'Documentos & templates'],
      acao: 'Acessar',
      cor: '#111827',
      corTexto: '#c7d2fe',
      disponivel: true,
      abrir: () => onSelectProduct('escalone'),
    },
    {
      id: 'finance',
      marca: 'F',
      nome: 'Finance ERP',
      area: 'Financeiro',
      resumo: 'Contas a pagar e receber, compras e o resultado do mês.',
      recursos: ['Contas a pagar & receber', 'Compras, estoque & inventário', 'Painel financeiro'],
      acao: 'Acessar',
      cor: '#2563eb',
      corTexto: '#bfdbfe',
      disponivel: true,
      abrir: () => onSelectProduct('finance'),
    },
    {
      id: 'atlas',
      marca: 'A',
      nome: 'Atlas HCM',
      area: 'RH',
      resumo: 'Cadastro, perfil comportamental, avaliações e recrutamento.',
      recursos: ['Cadastro & organograma', 'Perfil DISC & competências', 'Vagas & treinamentos'],
      acao: 'Acessar',
      cor: '#7c3aed',
      corTexto: '#ddd6fe',
      disponivel: true,
      abrir: () => onSelectProduct('atlas'),
    },
    {
      id: 'sales',
      marca: 'S',
      nome: 'Atlas Sales',
      area: 'CRM',
      resumo: 'Funil de vendas, leads e atendimento — cada cliente com o seu banco.',
      recursos: ['Funis & oportunidades', 'Leads & contatos', 'WhatsApp & atendimento'],
      acao: 'Abrir',
      cor: '#059669',
      corTexto: '#a7f3d0',
      disponivel: true,
      abrir: () => window.open(ENDERECO_ATLAS_SALES, '_blank', 'noopener'),
    },
  ];

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#0a0f14] flex flex-col">
      {/* o fundo: escuro e quieto, para as quatro cores das marcas aparecerem */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_8%,rgba(99,102,241,0.16),transparent_42%),radial-gradient(circle_at_88%_12%,rgba(5,150,105,0.14),transparent_42%),radial-gradient(circle_at_50%_100%,rgba(37,99,235,0.12),transparent_50%)]" />
      <div className="absolute inset-0 opacity-[0.25] bg-[linear-gradient(to_right,rgba(148,163,184,0.06)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.06)_1px,transparent_1px)] bg-[size:64px_64px]" />

      <header className="relative px-6 sm:px-10 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="h-7 w-7 rounded-lg bg-white/10 border border-white/10 flex items-center justify-center text-white text-xs font-black">
            E
          </span>
          <span className="text-white/70 text-sm font-medium tracking-wide">Escalone</span>
        </div>
        <div className="flex items-center gap-2.5 pl-1 pr-3 py-1 rounded-full bg-white/[0.06] border border-white/10">
          <span className="h-7 w-7 rounded-full bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center text-white text-xs font-bold">
            {primeiroNome.charAt(0).toUpperCase()}
          </span>
          <span className="text-sm text-white/80">{primeiroNome}</span>
        </div>
      </header>

      <main className="relative flex-1 flex items-center justify-center px-4 sm:px-8 py-6">
        <div className="w-full max-w-6xl">
          <div className="mb-10 sm:mb-12">
            <h1 className="text-3xl sm:text-4xl font-semibold text-white tracking-tight">
              {saudacao()}, {primeiroNome}.
            </h1>
            <p className="text-white/45 mt-2">
              Por onde você quer começar?
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {produtos.map((p) => (
              <button
                key={p.id}
                onClick={p.abrir}
                disabled={!p.disponivel}
                className={`group relative text-left rounded-2xl p-6 border transition-all duration-300 flex flex-col
                  ${p.disponivel
                    ? 'bg-white/[0.055] border-white/10 hover:bg-white/[0.09] hover:border-white/20 hover:-translate-y-1 cursor-pointer'
                    : 'bg-white/[0.02] border-white/[0.06] cursor-not-allowed'}
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0f14]`}
              >
                {/* o brilho da marca, só quando o cartão está vivo e sob o cursor */}
                {p.disponivel && (
                  <span
                    className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ boxShadow: `0 18px 50px -22px ${p.cor}, inset 0 1px 0 rgba(255,255,255,0.06)` }}
                  />
                )}

                <div className="relative flex items-start justify-between mb-5">
                  <span
                    className={`h-12 w-12 rounded-xl flex items-center justify-center text-xl font-black text-white
                      transition-transform duration-300 ${p.disponivel ? 'group-hover:scale-105' : 'opacity-45'}`}
                    style={{ background: p.cor, boxShadow: `0 8px 24px -10px ${p.cor}` }}
                  >
                    {p.marca}
                  </span>
                </div>

                <h2 className={`relative text-lg font-semibold mb-1.5 flex items-baseline gap-2 flex-wrap
                  ${p.disponivel ? 'text-white' : 'text-white/45'}`}>
                  {p.nome}
                  <span className="text-xs font-medium tracking-wide" style={{ color: p.corTexto }}>
                    {p.area}
                  </span>
                </h2>
                <p className={`relative text-sm leading-relaxed mb-5 ${p.disponivel ? 'text-white/50' : 'text-white/25'}`}>
                  {p.resumo}
                </p>

                <ul className="relative space-y-1.5 mb-6 flex-1">
                  {p.recursos.map((r) => (
                    <li key={r} className={`text-[13px] flex items-center gap-2 ${p.disponivel ? 'text-white/55' : 'text-white/25'}`}>
                      <span className="h-1 w-1 rounded-full flex-shrink-0"
                        style={{ background: p.disponivel ? p.corTexto : 'rgba(255,255,255,0.2)' }} />
                      {r}
                    </li>
                  ))}
                </ul>

                <span className={`relative flex items-center gap-1.5 text-sm font-medium
                  ${p.disponivel ? 'text-white group-hover:gap-2.5' : 'text-white/30'} transition-all`}>
                  {p.disponivel ? p.acao : 'Em preparação'}
                  {p.disponivel && <ArrowRight className="h-4 w-4" />}
                </span>
              </button>
            ))}
          </div>
        </div>
      </main>

      <footer className="relative px-6 sm:px-10 py-5 flex items-center justify-between text-xs text-white/25">
        <span>© 2026 Escalone</span>
        <span className="hidden sm:inline">Um login para todos os sistemas do grupo</span>
      </footer>
    </div>
  );
};

export default ProductSelector;
